import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { env } from "@/app/config/env";
import {
  getAppRequestsInFlight,
  setAppRequestsBlocked,
  subscribeAppRequests,
  subscribeUpdateRequired,
} from "@/services/http/app-compatibility";
import { usePwaUpdate as useWorker } from "./usePwaUpdate";
import { UpdateContext, type CheckStatus } from "./UpdateContext";
import {
  appInformationSchema,
  componentSchema,
  compareVersions,
  needsRequiredUpdate,
  unseenReleases,
  type AppInformation,
  type ComponentRelease,
} from "./release-information";

const SEEN_KEY = "kayer.release-notes.seen.v1";
function readSeen(): Record<string, string> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(SEEN_KEY) ?? "{}");
    return value && typeof value === "object"
      ? Object.fromEntries(
          Object.entries(value).filter(([, v]) => typeof v === "string"),
        )
      : {};
  } catch {
    return {};
  }
}
async function json(url: string, signal: AbortSignal) {
  const response = await fetch(url, {
    cache: "no-store",
    credentials: "omit",
    signal,
    referrerPolicy: "no-referrer",
  });
  if (!response.ok) throw new Error("Update information unavailable");
  return response.json() as Promise<unknown>;
}

/** One identity-independent owner for worker updates, compatibility and release announcements. */
export function AppUpdateProvider({ children }: { children: ReactNode }) {
  const worker = useWorker();
  const { checkForUpdate: checkWorker } = worker;
  const [information, setInformation] = useState<AppInformation | null>(null);
  const [frontend, setFrontend] = useState<ComponentRelease | null>(null);
  const [status, setStatus] = useState<CheckStatus>("idle");
  const [required, setRequired] = useState(false);
  const [seen, setSeen] = useState(readSeen);
  const inFlight = useSyncExternalStore(
    subscribeAppRequests,
    getAppRequestsInFlight,
    () => 0,
  );
  const checking = useRef(false);
  const alive = useRef(true);
  const check = useCallback(
    async (manual = false) => {
      if (checking.current) return;
      checking.current = true;
      setStatus("checking");
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 10000);
      try {
        let manifest: ComponentRelease | null = null;
        let metadataAvailable = false;
        try {
          const info = appInformationSchema.parse(
            await json(
              new URL("/api/app-info", env.VITE_API_BASE_URL).href,
              controller.signal,
            ),
          );
          if (!alive.current) return;
          setInformation(info);
          const unsupported = needsRequiredUpdate(
            env.VITE_APP_VERSION,
            info.policy,
          );
          setRequired(unsupported);
          setAppRequestsBlocked(unsupported);
          manifest = info.frontend;
          metadataAvailable = true;
        } catch {
          // Keep a known compatibility block, but do not label stale API information as current.
          if (alive.current) setInformation(null);
        }
        if (!manifest) {
          try {
            manifest = componentSchema.parse(
              await json("/release-info.json", controller.signal),
            );
          } catch {
            if (!metadataAvailable) throw new Error("Update discovery failed");
          }
        }
        if (!alive.current) return;
        setFrontend(manifest);
        if (!navigator.onLine) {
          setStatus("offline");
          return;
        }
        const support = window.isSecureContext && "serviceWorker" in navigator;
        if (!support) {
          setStatus("unsupported");
          return;
        }
        if (!manifest) {
          setStatus("failed");
          return;
        }
        await checkWorker(manual);
        if (!alive.current) return;
        const newer =
          (compareVersions(manifest.version, env.VITE_APP_VERSION) ?? 0) > 0;
        setStatus(newer ? "downloading" : "current");
      } catch {
        if (alive.current) setStatus(navigator.onLine ? "failed" : "offline");
      } finally {
        window.clearTimeout(timer);
        checking.current = false;
      }
    },
    [checkWorker],
  );

  useEffect(() => {
    alive.current = true;
    const off = subscribeUpdateRequired(() => {
      setRequired(true);
      void check();
    });
    const refresh = () => {
      if (document.visibilityState !== "hidden") void check();
    };
    refresh();
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", refresh);
    const storage = (event: StorageEvent) => {
      if (event.key === SEEN_KEY) setSeen(readSeen());
    };
    window.addEventListener("storage", storage);
    return () => {
      alive.current = false;
      off();
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      window.removeEventListener("storage", storage);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [check]);

  const announcements = [
    ...unseenReleases(frontend, seen.frontend ?? null).map((release) => ({
      component: "frontend",
      release,
    })),
    ...unseenReleases(information?.backend ?? null, seen.backend ?? null).map(
      (release) => ({ component: "backend", release }),
    ),
  ];
  const dismissAnnouncements = () => {
    const next = { ...seen };
    if (frontend) next.frontend = frontend.version;
    if (information?.backend) next.backend = information.backend.version;
    setSeen(next);
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
    } catch {
      /* Memory dismissal works without storage. */
    }
  };
  const updateApp = async () => {
    // This includes anonymous approvals and sends; never abandon their in-flight outcome.
    if (getAppRequestsInFlight() > 0) return;
    if (
      window.confirm(
        "برنامه دوباره بارگذاری می‌شود. ابتدا از ذخیره شدن کار خود مطمئن شوید. ادامه می‌دهید؟",
      )
    )
      await worker.updateApp();
  };
  return (
    <UpdateContext.Provider
      value={{
        ...worker,
        information,
        frontend,
        required,
        inFlight,
        announcements,
        dismissAnnouncements,
        status: worker.updateFailed
          ? "failed"
          : worker.needRefresh
            ? "ready"
            : worker.isDownloading
              ? "downloading"
              : status,
        checkForUpdate: () => check(true),
        updateApp,
      }}
    >
      {children}
    </UpdateContext.Provider>
  );
}
